import { Dispatch, SetStateAction, useState } from 'react'
import { Link } from 'react-router'
import { ContentCopy } from '@mui/icons-material'
import { Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material'

import { useAuthentication } from '../../providers/auth-provider'
import { ShipToInformation } from '../../routes/accounts/components/orders-tab-panel/order-accordion-details'
import { getFormattedDateTime } from '../../utils/helpers'
import useClipboard from '../../utils/hooks/useClipboard'

import ChangeOrderDetailsModal from './change-order-details-modal'
import useStyles from './index.styles'
import OrderField from './order-field'
import OrderTimeline from './order-timeline'

const OrderInfo = ({ orderDetail, setOrderRefreshCounter }: { orderDetail: IOrderDetail; setOrderRefreshCounter: Dispatch<SetStateAction<number>> }) => {
  const classes = useStyles

  const { clickToCopy } = useClipboard()
  const { hasInternalUserAccess } = useAuthentication()

  const [showChangeOrderDetailsModal, setShowChangeOrderDetailsModal] = useState<boolean>(false)

  const events = orderDetail.events || []
  const trackingUpdatedAt = events[events.length - 1]?.eventTime || orderDetail?.trackingNumberTs

  return (
    <Grid
      container={true}
      sx={{
        gap: 2,
      }}
    >
      <Grid size={2.9}>
        <OrderTimeline events={orderDetail.events} orderType="order" />
      </Grid>
      <Grid size="auto">
        <Divider orientation="vertical" variant="middle" />
      </Grid>
      <Grid
        container={true}
        spacing={2}
        size={3}
        sx={{
          alignContent: 'flex-start',
        }}
      >
        <Grid size={12}>
          <Grid
            container={true}
            size={12}
            sx={{
              justifyItems: 'center',
            }}
          >
            <Grid size={12}>
              <Typography variant="overline" sx={classes.summaryFieldTitle}>
                Tracking Number
              </Typography>
            </Grid>

            <Grid container={true} size={12}>
              <Typography variant="body1">
                {orderDetail.trackingNumber && orderDetail.trackingNumber.startsWith('1Z') && (
                  <Link to={`https://wwwapps.ups.com/tracking/tracking.cgi?tracknum=${orderDetail.trackingNumber}`} target="_blank">
                    {orderDetail.trackingNumber}
                  </Link>
                )}
                {orderDetail.trackingNumber && !orderDetail.trackingNumber.startsWith('1Z') && orderDetail.trackingNumber}
                {!orderDetail.trackingNumber && '-'}
              </Typography>

              {orderDetail.trackingNumber && (
                <Tooltip title="Copy tracking number to clipboard" arrow={true} placement="top">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      clickToCopy(orderDetail.trackingNumber ?? '')
                    }}
                    sx={classes.copyIconButton}
                    aria-label="Copy tracking number to clipboard"
                  >
                    <ContentCopy color="primary" sx={classes.copyIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>

            {trackingUpdatedAt && (
              <Grid size={12}>
                <Typography variant="caption">Updated {getFormattedDateTime(trackingUpdatedAt)}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {orderDetail.shipToAddress && (
          <Grid size={12}>
            <ShipToInformation shipToAddress={orderDetail.shipToAddress} />
          </Grid>
        )}
      </Grid>
      <Grid
        container={true}
        spacing={2}
        size="grow"
        sx={{
          alignContent: 'flex-start',
        }}
      >
        <OrderField xs={6} label="Short Order" value={orderDetail.shortOrderNumber} />
        <OrderField xs={6} label="Created" value={getFormattedDateTime(orderDetail.createdAt)} />
        <OrderField xs={6} label="Order No" value={orderDetail.orderNumber} />
        <OrderField xs={6} label="Last Updated" value={orderDetail.updatedAt ? getFormattedDateTime(orderDetail.updatedAt) : ''} />
        <OrderField
          xs={6}
          label="Full Kit"
          value={
            typeof orderDetail.fullKit === 'boolean' ? orderDetail.fullKit.toString().charAt(0).toUpperCase() + orderDetail.fullKit.toString().slice(1) : 'N/A'
          }
        />
        <OrderField xs={6} label="Type" value={orderDetail.type} />
        <OrderField xs={6} label="Kit No" value={orderDetail.kitNumber || 'N/A'} />
        <OrderField
          xs={6}
          label="Chargeable"
          value={orderDetail.chargable}
          canEdit={hasInternalUserAccess}
          onEdit={() => setShowChangeOrderDetailsModal(true)}
        />
      </Grid>
      <ChangeOrderDetailsModal
        open={showChangeOrderDetailsModal}
        setOpen={setShowChangeOrderDetailsModal}
        currentChargeableValue={orderDetail.chargable}
        nodeId={orderDetail.orderNodeId}
        setOrderRefreshCounter={setOrderRefreshCounter}
      />
    </Grid>
  )
}

export default OrderInfo
